/**
 * 企业空间树展开规则：
 * 1. 展开企业空间： 默认树结构展开根团队及下一级团队
 *    1.1 默认勾选企业空间情况下：
 *      1.1.1 一级和二级都是勾选态
 *      1.1.2 展开二级时子团队也都是勾选的
 *      1.1.3 取消选择团队时只取消自己，若该节点有子节点且并未拉取过，需要展开树并且将子节点勾选上
 *      1.1.4 取消选择团队时只取消自己，若该节点有子节点且并拉取过，仅需要展开树，子节点不联动
 *    1.2 展开时不勾选企业空间情况下：
 *      1.2.1 一级和二级都是未勾选态
 *      1.2.2 展开二级时子团队也都是不勾选的
 *      1.2.3 勾选团队时只勾选自己，若该节点有子节点且并未拉取过，需要展开树并且子节点不进行勾选
 *      1.2.4 勾选团队时只勾选自己，若该节点有子节点且并拉取过，仅需要展开树，子节点不联动
 * 2. 节点拉取过子节点，但进行收起，此时子节点若是选中态，也算作是勾选的，需要进行搜索；子节点若是未勾选态，不进行搜索。
 */

import { computed, reactive, ref } from '@vue/composition-api';
import { TreeNodeModel, TreeOptionData } from 'tdesign-vue';

import { SMH } from '@/renderer/scripts/smh';
import teamHandler from '@/renderer/views/desktop/ManageConsole/TeamManagement/compositions/teamHandler';

class TeamSpaceCache {
  private teamSpaceItems = new Map<SMH.SpaceId, SMH.SpaceItem>();

  get items() {
    return [...this.teamSpaceItems.values()];
  }

  constructor() {
    this.teamSpaceItems = new Map<SMH.SpaceId, SMH.SpaceItem>();
  }

  deleteSpace(spaceId: SMH.SpaceId) {
    this.teamSpaceItems.delete(spaceId);
  }

  addSpace(spaceId: SMH.SpaceId, includeChild = true) {
    const spaceItem = { spaceId, includeChildSpace: includeChild };
    this.teamSpaceItems.set(spaceId, spaceItem);
  }

  addSpaces(spaceIds: SMH.SpaceId[], includeChild = true) {
    spaceIds.forEach(spaceId => {
      this.addSpace(spaceId, includeChild);
    });
  }

  removeChildSpaceInclude(parentSpaceId: SMH.SpaceId) {
    const parentSpaceItem = this.teamSpaceItems.get(parentSpaceId);
    if (parentSpaceItem) {
      parentSpaceItem.includeChildSpace = false;
    }
  }

  clear() {
    this.teamSpaceItems.clear();
  }
}

interface TeamData {
  list: SMH.TeamTreeNode[]; // 企业树节点的信息
  expanded: SMH.SpaceId[]; // 企业树展开的企业空间
  checked: SMH.SpaceId[]; // 企业树选中的企业空间
  teamSpaceChecked: boolean; // 企业空间是否选中
}

export default function handler() {
  const teamTreeKey = {
    value: 'spaceId',
    label: 'name',
    children: 'children',
  };

  const currLoadedTeamNodeSpace = ref<SMH.SpaceId[]>([]); // 缓存当前企业树已加载的节点数
  const teamSpaceCache = new TeamSpaceCache();

  const rootId = ref('');
  const teamData = reactive<TeamData>({
    list: [],
    expanded: [],
    checked: [],
    teamSpaceChecked: true,
  });

  // 企业空间仅选中了部分而非全选或全不选
  const teamIndeterminate = computed(() => {
    return teamData.checked.length < currLoadedTeamNodeSpace.value.length && teamData.checked.length !== 0;
  });

  const loadingTeamTree = ref(false);
  const { listTeam } = teamHandler(1, false);

  const methods = {
    async initData() {
      teamSpaceCache.clear();
      Object.assign(teamData, {
        list: [],
        expanded: [],
        checked: [],
        teamSpaceChecked: true,
      });

      loadingTeamTree.value = true;
      const res = await listTeam({ teamId: '', withPath: false });
      loadingTeamTree.value = false;
      teamData.list = [res];

      rootId.value = res.spaceId;
      teamData.expanded = [rootId.value];
      teamSpaceCache.addSpace(rootId.value);

      const loadSpaceIds = [rootId.value];
      if (!res.children) {
        res.children = [];
      }
      res.children.forEach((item: any) => {
        item.children = item.children && item.children.length > 0;
        // 初始化团队树要选中根团队+一级团队
        loadSpaceIds.push(item.spaceId);
        teamSpaceCache.addSpace(item.spaceId);
      });
      currLoadedTeamNodeSpace.value = loadSpaceIds;
      teamData.checked = loadSpaceIds;

      // teamData.value = res.children;
      teamData.list = [res];
    },
    getTeamSpaceItems() {
      const spaceItems = teamSpaceCache.items;
      return spaceItems;
    },
    // 加载企业空间树节点的子节点
    async loadTeamTreeNode(node: TreeNodeModel) {
      const teamId = node.data?.id || '';
      node.data.loaded = true;
      const res = await listTeam({ teamId, withPath: false });
      const subTeam = (res.children as SMH.TeamTreeNode[]).map(item => ({
        ...item,
        children: item.children && item.children.length > 0,
      }));
      if (!node.data.children || !Array.isArray(node.data.children)) {
        node.data.children = [];
      }
      const subTeamSpaces = subTeam.map(item => item.spaceId);
      currLoadedTeamNodeSpace.value = [...currLoadedTeamNodeSpace.value, ...subTeamSpaces];
      node.data.children.push(...(subTeam as TreeOptionData[]));
      node.appendData?.(subTeam as TreeOptionData[]);
      return subTeam;
    },
    // 选择企业的节点
    async changeTeamNode(
      value: SMH.SpaceId[],
      context: {
        node: TreeNodeModel;
      },
    ) {
      const { teamSpaceChecked } = teamData;
      const { node } = context;
      const currNodeSpaceId = node.value as SMH.SpaceId;
      const isAppendChecked = value.length - teamData.checked.length > 0; // 当前操作是选中态
      /**
       * （此处有一个记忆交互依赖，即依赖企业空间上一次是否选中态）
       * 如果是企业空间当前是半勾选，但之前是选中态，当前树节点为取消选中操作，且有子节点
       * 1. 若子节点未load出来，则将其load出来后联动选中
       * 2. 若子节点已load出来，则仅展开树，但不联动选中
       *
       * 如果是企业空间当前是半勾选，但之前是非选中态，当前树节点为选中操作，且有子节点
       * 1. 若子节点未load出来，则将其load出来后联动不选中
       * 2. 若子节点已load出来，则仅展开树，但不联动选中
       */
      const hasChildren = !!node.data.children;
      const childrenIsLoad = typeof node.data.children !== 'boolean';
      if (!childrenIsLoad) {
        const subTeam = await methods.loadTeamTreeNode(node);
        teamData.expanded = [...teamData.expanded, currNodeSpaceId];
        if (teamSpaceChecked) {
          teamData.checked = [...value, ...subTeam.map(item => item.spaceId)];
        } else {
          teamData.checked = value;
        }
        const childrens = node.getChildren(false);
        if (typeof childrens !== 'boolean') {
          childrens.forEach(item => {
            item.checked = teamSpaceChecked;
            teamSpaceChecked && teamSpaceCache.addSpace(item.value as SMH.SpaceId);
          });
        }
      } else {
        teamData.checked = value;
        if (hasChildren && !node.expanded) {
          teamData.expanded = [...teamData.expanded, currNodeSpaceId];
        }
      }

      if (!isAppendChecked) {
        // 若为取消选中，向上找，所有父节点的 includeChildSpace （包含子空间字段）都需要关闭
        let parentNode = node.getParent();
        while (parentNode) {
          const parentSpaceId = parentNode?.value as SMH.SpaceId;
          teamSpaceCache.removeChildSpaceInclude(parentSpaceId as SMH.SpaceId);
          parentNode = parentNode.getParent();
        }
        teamSpaceCache.deleteSpace(currNodeSpaceId);
      } else {
        const includeChild = teamData.checked.length === currLoadedTeamNodeSpace.value.length;
        teamSpaceCache.addSpace(currNodeSpaceId, includeChild);
      }
    },
    async expandTeamNode(
      value: SMH.SpaceId[],
      context: {
        node: TreeNodeModel;
      },
    ) {
      const { node } = context;
      const childrenIsLoad = typeof node.data.children !== 'boolean';
      const teamSpaceIsSelected = teamData.teamSpaceChecked;
      if (!childrenIsLoad) {
        const subTeam = await methods.loadTeamTreeNode(context.node);
        if (teamSpaceIsSelected) {
          teamData.checked = [...teamData.checked, ...subTeam.map(item => item.spaceId)];
          const childrens = context.node.getChildren(false);
          if (typeof childrens !== 'boolean') {
            childrens.forEach(item => {
              item.checked = true;
              teamSpaceCache.addSpace(item.value as SMH.SpaceId);
            });
          }
        }
      }
    },
    triggerTeamSpaceChecked() {
      /**
       * 企业空间默认为勾选态（共三种状态：全选、半选、未勾选）
       *  a.点击半选、未勾选 --> 状态变更为：全选
       *  b.点击全选 --> 状态变更为：未勾选
       */
      if (teamIndeterminate.value) {
        teamData.teamSpaceChecked = true;
      } else {
        teamData.teamSpaceChecked = !teamData.teamSpaceChecked;
      }
      teamSpaceCache.clear();
      if (teamData.teamSpaceChecked) {
        teamData.checked = [...currLoadedTeamNodeSpace.value];
        teamSpaceCache.addSpaces(currLoadedTeamNodeSpace.value);
      } else {
        teamData.checked = [];
      }
    },
  };

  return { loadingTeamTree, teamTreeKey, teamData, teamIndeterminate, methods };
}
